import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { ApplicationError } from "../../../models/errors/application-error";
import SupplierService from "../../../services/supplier/supplier-service";
import { ApplicationSupplierProductsList } from "../../../models/supplier/application-supplier-product-list";
import { showError, showSuccess } from "../../../components/notification/toastr-actions";
import { ApplicationSupplierProductDetail } from "../../../models/supplier/application-supplier-product-detail";
import { ApplicationProductList } from "../../../models/product/application-product-list";
import ProductService from "../../../services/product/product-service";
import { ApplicationSupplierProductHeader } from "../../../models/supplier/application-supplier-product-header";
import { ApplicationSizeUnit } from "../../../models/product/application-size-unit";
import { FetchParams } from "../../../components/controls/table-view";
import { ApplicationProductOverride } from "../../../models/product/application-product-override";
import { ApplicationProductOverrideList } from "../../../models/product/application-product-override-list";


export const fetchSupplierProducts = createAsyncThunk<ApplicationSupplierProductsList | null, { supplierId: number, unmappedOnly: boolean, fetchParams: FetchParams }, { state: RootState }>(
    'supplierProducts/get',
    async ({ supplierId, fetchParams, unmappedOnly }, { getState, rejectWithValue, dispatch }) => {
        const state = getState();
        const supplierService = new SupplierService();

        try {
            var currentPosition: number = state.supplierProducts?.products?.currentPosition ?? 0;
            var fetchNext: number = 15;
            var count: number = state.supplierProducts.products?.count ?? -1;

            if (fetchParams.reset) {
                currentPosition = 0;
                count = -1;
                dispatch(resetSupplierProducts());
            }

            if (currentPosition != count) {
                return await supplierService.getProducts(supplierId, currentPosition, fetchNext, false, unmappedOnly, fetchParams.searchText)
            }
            return null;
        } catch (error: any) {

            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);


export const fetchSupplierProduct = createAsyncThunk<ApplicationSupplierProductDetail | null, number, { state: RootState }>(
    'supplierProduct/get',
    async (supplierProductId, { rejectWithValue }) => {
        const supplierService = new SupplierService();
        try {
            return await supplierService.getProduct(supplierProductId)
        } catch (error: any) {

            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const fetchSupplierProductComparisons = createAsyncThunk<ApplicationSupplierProductHeader[] | null, number, { state: RootState }>(
    'supplierProduct/comparison/get',
    async (productId, { rejectWithValue }) => {
        const supplierService = new SupplierService();
        try {
            return await supplierService.getProductComparisons(productId)
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const searchSupplierProducts = createAsyncThunk<ApplicationProductList | null, { supplierId: number, searchText: string, fetchAll: boolean }, { state: RootState }>(
    'supplierProducts/search',
    async ({ supplierId, searchText, fetchAll }, { getState, rejectWithValue }) => {
        const state = getState();
        const productService = new ProductService();
        try {
            var currentPosition: number = state.supplierProducts.searchProducts?.currentPosition ?? 0;
            var fetchNext: number = 20;
            var count: number = state.supplierProducts.searchProducts?.count ?? -1;

            if (currentPosition != count) {
                return await productService.searchSupplierProducts(supplierId, searchText, currentPosition, fetchNext, fetchAll);
            }

            return null;
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);


export const mapProduct = createAsyncThunk<ApplicationSupplierProductDetail | null, number, { state: RootState }>(
    'supplierProduct/mapupdate',
    async (productId, { dispatch, getState, rejectWithValue }) => {
        const supplierService = new SupplierService();
        try {
            const supplierProductState = getState().supplierProducts;
            if (supplierProductState.selectedUnmappedProduct) {
                var product = await supplierService.getProduct(supplierProductState.selectedUnmappedProduct.id);
                product.productId = productId;
                return await supplierService.updateProduct(product)
            }

            dispatch(showError("No product selected to map"))
            return null;
        } catch (error: any) {

            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const updateSupplierProduct = createAsyncThunk<ApplicationSupplierProductDetail | null, ApplicationSupplierProductDetail, { state: RootState }>(
    'supplierProduct/update',
    async (product, { getState, rejectWithValue }) => {
        const supplierService = new SupplierService();
        try {

            return await supplierService.updateProduct(product)
        } catch (error: any) {

            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const restoreSupplierProduct = createAsyncThunk<ApplicationSupplierProductDetail, { supplierProductId: number }, { state: RootState }>(
    'supplierProduct/restore',
    async ({ supplierProductId }, { getState, rejectWithValue }) => {
        const supplierService = new SupplierService();
        try {
            return await supplierService.restoreFromHistory(supplierProductId)
        } catch (error: any) {

            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);


export const updateProductSizeUnit = createAsyncThunk<ApplicationSupplierProductHeader[] | null, { productId: number, newSizeUnit: ApplicationSizeUnit }, { state: RootState }>(
    'supplierProduct/sizeunit/update',
    async ({ productId, newSizeUnit }, { getState, rejectWithValue }) => {
        const supplierService = new SupplierService();
        try {
            return await supplierService.updateProductSizeUnit(productId, newSizeUnit)
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const insertSupplierProduct = createAsyncThunk<ApplicationSupplierProductDetail | null, ApplicationSupplierProductDetail, { state: RootState }>(
    'supplierProduct/insert',
    async (product, { getState, rejectWithValue }) => {
        const supplierService = new SupplierService();
        try {
            return await supplierService.insertProduct(product)
        } catch (error: any) {

            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);


export const deleteProduct = createAsyncThunk<number, number, { state: RootState }>(
    'supplierProduct/delete',
    async (supplierProductId, { rejectWithValue }) => {
        const supplierService = new SupplierService();
        try {
            await supplierService.deleteProduct(supplierProductId)
            return supplierProductId;
        } catch (error: any) {

            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);


export const insertSupplierProductPrice = createAsyncThunk<{ id: number, price: number } | null, number, { state: RootState }>(
    'supplierProductsPrice/insert',
    async (supplierProductId, { getState, dispatch, rejectWithValue }) => {

        const supplierService = new SupplierService();
        try {

            const state = getState();
            var product = state.supplierProducts?.products?.products.find(x => x.id == supplierProductId);

            if (!product) {
                throw new ApplicationError("No product found");
            }

            await supplierService.insertProductPrice(supplierProductId, product.price ?? 0);
            dispatch(showSuccess("Price updated"))
            return { id: supplierProductId, price: product.price ?? 0 }

        } catch (error: any) {

            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const insertProductOverride = createAsyncThunk<ApplicationProductOverride | undefined, { supplierId: number, productId: number }, { state: RootState }>(
    'productOverride/insert',
    async ({ supplierId, productId }, { rejectWithValue }) => {
        const supplierService = new SupplierService();
        try {
            var override = await supplierService.insertProductOverride(productId, supplierId)
            return override;
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const deleteProductOverride = createAsyncThunk<number, { productOverrideId: number }, { state: RootState }>(
    'productOverride/delete',
    async ({ productOverrideId }, { rejectWithValue }) => {
        const supplierService = new SupplierService();
        try {
            await supplierService.deleteProductOverride(productOverrideId)
            return productOverrideId;
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const fetchProductOverrides = createAsyncThunk<ApplicationProductOverrideList | null, { fetchParams: FetchParams }, { state: RootState }>(
    'productOverride/get',
    async ({ fetchParams }, { getState, rejectWithValue, dispatch }) => {
        const state = getState();
        const supplierService = new SupplierService();

        try {
            var currentPosition: number = state.supplierProducts?.productOverrides?.currentPosition ?? 0;
            var fetchNext: number = 15;
            var count: number = state.supplierProducts.productOverrides?.count ?? -1;

            if (fetchParams.reset) {
                currentPosition = 0;
                count = -1;
                dispatch(resetProductOverrides());
            }

            if (currentPosition != count) {
                return await supplierService.getOverrides(currentPosition, fetchNext, false);
            }

            return null;
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

interface SupplierProductState {
    products: ApplicationSupplierProductsList | null
    productOverrides: ApplicationProductOverrideList | null
    selectedUnmappedProduct: ApplicationSupplierProductHeader | null
    loading: boolean,
    searchProducts: ApplicationProductList | null
    sizeUnitUpdatedProducts: ApplicationSupplierProductHeader[] | null
    comparisonProducts: ApplicationSupplierProductHeader[] | null
    comparisonProductId: number | null
    comparisonLoading: boolean
}

const initialState: SupplierProductState = {
    products: null,
    productOverrides: null,
    selectedUnmappedProduct: null,
    loading: false,
    searchProducts: null,
    sizeUnitUpdatedProducts: null,
    comparisonProducts: null,
    comparisonProductId: null,
    comparisonLoading: false
};

const supplierProductSlice = createSlice({
    name: 'supplierProducts',
    initialState,
    reducers: {
        resetSupplierProducts: (state) => {
            state.products = initialState.products;
        },
        resetProductOverrides: (state) => {
            state.productOverrides = initialState.productOverrides;
        },
        resetSearchProducts: (state) => {
            state.searchProducts = initialState.searchProducts
        },
        resetSizeUnitUpdatedProducts: (state) => {
            state.sizeUnitUpdatedProducts = initialState.sizeUnitUpdatedProducts
        },
        resetComparisonProducts: (state) => {
            state.comparisonProducts = initialState.comparisonProducts;
            state.comparisonProductId = initialState.comparisonProductId;
            state.comparisonLoading = initialState.comparisonLoading;
        },
        resetUnmappedSelectedProduct: (state) => {
            state.selectedUnmappedProduct = initialState.selectedUnmappedProduct
        },
        setUnmappedSelectedProduct: (state, action: PayloadAction<ApplicationSupplierProductHeader>) => {
            state.selectedUnmappedProduct = action.payload;
        },
        setMapping: (state, action: PayloadAction<number>) => {
            if (state.selectedUnmappedProduct) {
                state.selectedUnmappedProduct.productId = action.payload;
                state.sizeUnitUpdatedProducts = null;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSupplierProductComparisons.pending, (state, action) => {
                state.comparisonProductId = action.meta.arg;
                state.comparisonProducts = null;
                state.comparisonLoading = true;
            })
            .addCase(fetchSupplierProductComparisons.fulfilled, (state, action) => {
                state.comparisonLoading = false;
                state.comparisonProductId = action.meta.arg;
                state.comparisonProducts = action.payload;
            })
            .addCase(fetchSupplierProductComparisons.rejected, (state) => {
                state.comparisonLoading = false;
            })
            .addCase(fetchSupplierProducts.fulfilled, (state, action: PayloadAction<ApplicationSupplierProductsList | null>) => {
                if (action.payload) {
                    if (state.products?.products) {
                        state.products.products = state.products.products.concat(action.payload!.products);
                        state.products.currentPosition = action.payload!.currentPosition
                        state.products.count = action.payload!.count;
                    } else {
                        state.products = action.payload;
                    }
                }
            })
            .addCase(updateProductSizeUnit.fulfilled, (state, action: PayloadAction<ApplicationSupplierProductHeader[] | null>) => {
                state.sizeUnitUpdatedProducts = action.payload;
            })
            .addCase(deleteProduct.fulfilled, (state, action: PayloadAction<number>) => {
                if (state.products?.products) {
                    state.products.products = state.products?.products.filter(x => x.id != action.payload)
                }
            })
            .addCase(fetchProductOverrides.fulfilled, handleProductOverridesFulfilled)
            .addCase(searchSupplierProducts.fulfilled, handleSearchProductsFulfilled)
            .addCase(updateSupplierProduct.fulfilled, handleUpdateProduct)
            .addCase(restoreSupplierProduct.fulfilled, handleUpdateProduct)
            .addCase(mapProduct.fulfilled, handleUpdateProduct)
            .addCase(insertSupplierProduct.fulfilled, handleInsertProduct)
            .addCase(insertProductOverride.fulfilled, handleInsertOverride)
            .addCase(deleteProductOverride.fulfilled, handleRemoveOverride)

    },
});

const handleRemoveOverride = (
    state: SupplierProductState,
    action: PayloadAction<number>
) => {
    if (!action.payload)
        return;

    if (state.products?.products) {
        const product = state.products.products.find(
            (x) => x.productOverride?.id === action.payload
        );

        if (product) {
            product.productOverride = undefined;
            product.className = ""
        }
    }

    if (state.productOverrides?.overrides) {
        const originalLength = state.productOverrides.overrides.length;
        state.productOverrides.overrides = state.productOverrides.overrides.filter(
            (x) => x.id !== action.payload
        );

        if (state.productOverrides.overrides.length !== originalLength) {
            state.productOverrides.count = Math.max(0, state.productOverrides.count - 1);
            state.productOverrides.currentPosition = Math.max(0, state.productOverrides.currentPosition - 1);
        }
    }
};

const handleInsertOverride = (
    state: SupplierProductState,
    action: PayloadAction<ApplicationProductOverride | undefined>
) => {
    const override = action.payload;
    if (!override)
        return;

    if (state.products?.products) {
        const product = state.products.products.find(
            (x) => x.productId === override.productId
        );

        if (product) {
            product.productOverride = override;
            product.className = "highlight"
        }
    }

    if (state.productOverrides && !state.productOverrides.overrides.find((x) => x.id === override.id)) {
        state.productOverrides.overrides = [override, ...state.productOverrides.overrides];
        state.productOverrides.count += 1;
        state.productOverrides.currentPosition += 1;
    }
};

const handleProductOverridesFulfilled = (
    state: SupplierProductState,
    action: PayloadAction<ApplicationProductOverrideList | null>
) => {
    if (!action.payload) {
        return;
    }

    if (state.productOverrides?.overrides) {
        state.productOverrides.overrides = state.productOverrides.overrides.concat(action.payload.overrides);
        state.productOverrides.currentPosition = action.payload.currentPosition;
        state.productOverrides.count = action.payload.count;
    } else {
        state.productOverrides = action.payload;
    }
};

const handleInsertProduct = (
    state: SupplierProductState,
    action: PayloadAction<ApplicationSupplierProductDetail | null>
) => {
    if (action.payload && state.products?.products) {
        const insertedProduct = action.payload;
        state.products.products.push(mapToSupplierProductHeader(insertedProduct));
        state.products.count += 1;
        state.products.currentPosition += 1;
    }
};

const handleUpdateProduct = (
    state: SupplierProductState,
    action: PayloadAction<ApplicationSupplierProductDetail | null>
) => {
    if (action.payload && state.products?.products) {
        const updatedProduct = action.payload;

        state.products.products = state.products.products.map(product =>
            product.id === updatedProduct.id ?
                mapToSupplierProductHeader(updatedProduct) : product
        );
    }
};

const handleSearchProductsFulfilled = (
    state: SupplierProductState,
    action: PayloadAction<ApplicationProductList | null>
) => {
    if (action.payload) {
        const { products, currentPosition, count } = action.payload;
        if (state.searchProducts) {
            state.searchProducts.products = state.searchProducts.products.concat(products);
            state.searchProducts.currentPosition = currentPosition;
            state.searchProducts.count = count;
        } else {
            state.searchProducts = action.payload;
        }
    }
};

const mapToSupplierProductHeader = (model: ApplicationSupplierProductDetail) => {
    return {
        id: model.id,
        reference: model.reference,
        supplierId: model.supplierId,
        name: model.name,
        productId: model.productId,
        description: model.description,
        image: model.image,
        size: model.size,
        origin: model.origin,
        hasHistory: model.hasHistory,
        openPricing: model.openPricing,
        sizeUnit: model.sizeUnit,
        minimumOrderQuantity: model.minimumOrderQuantity,
        plu: model.plu,
        updated: model.updated,
        active: model.active,
        price: model.price,
    } as ApplicationSupplierProductHeader
}

export const { resetSupplierProducts, resetProductOverrides, resetSizeUnitUpdatedProducts, resetComparisonProducts, resetSearchProducts, resetUnmappedSelectedProduct, setUnmappedSelectedProduct, setMapping } = supplierProductSlice.actions;
export default supplierProductSlice.reducer;
